'use client'

import { useState, useSyncExternalStore, type ComponentProps } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import ActivityFeed from './activity-feed'
import ListingsFeed from './listings-feed'
import MempoolFeed from './mempool-feed'
import BidsFeed from './bids-feed'

const subscribeNoop = () => () => {}

export default function ActivityTabs({
	initialItems
}: Pick<ComponentProps<typeof ActivityFeed>, 'initialItems'>) {
	// Deep links from the Bids page (/activity?tab=listings), read on the client
	// so the ISR page stays static; a tab the user picks wins afterwards.
	const requested = useSyncExternalStore(
		subscribeNoop,
		() => new URLSearchParams(window.location.search).get('tab'),
		() => null
	)
	const [picked, setTab] = useState<string | null>(null)
	// Mempool stays the pending-sales feed (venue on each row). Bids is the
	// offers book added beside it. A picked tab wins over the query string.
	const tab =
		picked ??
		(requested === 'listings'
			? 'listings'
			: requested === 'bids'
			? 'bids'
			: requested === 'mempool'
			? 'mempool'
			: 'sales')

	return (
		<Tabs
			value={tab}
			onValueChange={setTab}
			className="flex h-[calc(100dvh-88px)] sm:h-[calc(100dvh-138px)] min-h-0 flex-col"
		>
			<h1 className="sr-only">Marketplace activity</h1>
			<TabsList
				aria-label="Activity feeds"
				className="grid h-11 shrink-0 grid-cols-4 rounded-none border-b border-border bg-transparent p-0"
			>
				{([['sales', 'Sales'], ['listings', 'Listings'], ['bids', 'Offers'], ['mempool', 'Mempool']] as const).map(([value, label]) => (
					<TabsTrigger
						key={value}
						value={value}
						className="h-11 rounded-none border-b-2 border-transparent text-xs data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none focus-visible:ring-inset focus-visible:ring-offset-0"
					>
						{label}
					</TabsTrigger>
				))}
			</TabsList>
			<TabsContent value="sales" className="mt-0 min-h-0 flex-1 overflow-hidden">
				<ActivityFeed
					initialItems={initialItems}
					itemsCount={initialItems.length}
					isFullPage
					hideHeader
					activitySource="merged"
				/>
			</TabsContent>
			<TabsContent value="listings" className="mt-0 min-h-0 flex-1 overflow-hidden">
				<ListingsFeed hideHeader onSwitchTab={() => setTab('sales')} />
			</TabsContent>
			<TabsContent value="mempool" className="mt-0 min-h-0 flex-1 overflow-hidden">
				<MempoolFeed hideHeader onSwitchTab={() => setTab('sales')} />
			</TabsContent>
			<TabsContent value="bids" className="mt-0 min-h-0 flex-1 overflow-hidden">
				<BidsFeed />
			</TabsContent>
		</Tabs>
	)
}
